import { serverOptionsFilter } from "@dehub/theme";
import { Box, Button, Menu, Popover, Select, Skeleton, Table, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { MagnifyingGlassIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { NetworkApi } from "../../apis";
import { ReturnsApi } from "../../apis/returns";
import { ListFooter, ListStates, resolveListStates, DateRangeChip, PartyCell, type DateRangeValue } from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { Page, PageHeader } from "../../components/layout/Page";
import { usePermission } from "../../components/permission";
import { ReturnDecisionDrawer } from "../../components/returns/return-decision";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { renderBusinessName } from "../../utils/render-business-name";
import { getReasonLabel } from "../../utils/return-constants";
import {
  getStageKey,
  nextActionFor,
  OVERDUE_DAYS,
  PRIMARY_ACTIONS,
  resolutionKey,
  STAGE_STATUSES,
  stageBars,
  WAITING_TABS,
  type BarState,
  type ReturnStage,
  type WaitingOn,
  type WaitingTab,
} from "../../utils/return-queue";
import classes from "./list.module.css";

const COLUMN_COUNT = 7;
const SKELETON_ROWS = 5;
const BAR_CLASS: Record<BarState, string> = {
  done: classes.barDone,
  now: classes.barNow,
  warn: classes.barWarn,
  wait: classes.barWait,
  todo: "",
};

type ListQuery = {
  waiting: WaitingTab;
  stage: string;
  partner: string | null;
  from: string | null;
  to: string | null;
  q: string;
  page: number;
  limit: number;
};

const DEFAULT_WAITING: WaitingTab = "me";

function readQuery(params: URLSearchParams): ListQuery {
  const w = params.get("waiting") as WaitingTab | null;
  const stage = params.get("stage") ?? "";
  return {
    waiting: w && WAITING_TABS.includes(w) ? w : DEFAULT_WAITING,
    stage: stage in STAGE_STATUSES ? stage : "",
    partner: params.get("partner") || null,
    from: params.get("from") || null,
    to: params.get("to") || null,
    q: params.get("q") || "",
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
    limit: Math.max(1, parseInt(params.get("limit") || "40", 10) || 40),
  };
}

function writeQuery(q: ListQuery, keep: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams();
  const ret = keep.get("ret");
  if (ret) p.set("ret", ret);
  if (q.waiting !== DEFAULT_WAITING) p.set("waiting", q.waiting);
  if (q.stage) p.set("stage", q.stage);
  if (q.partner) p.set("partner", q.partner);
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.q) p.set("q", q.q);
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== 40) p.set("limit", String(q.limit));
  return p;
}

export const RefundInvoiceList = () => {
  const { t } = useTranslation(["returns", "common"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { myBusinessType, user } = useSelector((state: { auth: IAuth }) => state.auth);
  const isBuyerBusiness = myBusinessType === "BUYER";
  const myBusinessId = user?.currentBusiness?.id;

  const q = useMemo(() => readQuery(searchParams), [searchParams]);
  const openId = searchParams.get("ret");

  const patchQuery = useCallback(
    (patch: Partial<ListQuery>, resetPage = true) => {
      setSearchParams(writeQuery({ ...q, ...patch, ...(resetPage ? { page: 1 } : {}) }, searchParams), { replace: true });
    },
    [q, searchParams, setSearchParams],
  );

  const openReturn = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("ret", id);
    setSearchParams(next);
  };
  const closeReturn = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("ret");
    setSearchParams(next, { replace: true });
  };

  // Хайлт URL-ээс тусдаа state: бичих бүрт хүсэлт явуулахгүй.
  const [searchInput, setSearchInput] = useState(q.q);
  const [searchDebounced] = useDebouncedValue(searchInput, 600);
  useEffect(() => {
    if (searchDebounced.trim() !== q.q) patchQuery({ q: searchDebounced.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced]);
  useEffect(() => {
    if (q.q !== searchDebounced.trim()) setSearchInput(q.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.q]);

  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerLabel, setPartnerLabel] = useState<string | null>(null);
  const { query: partnerSearch, setQuery: setPartnerSearch, data: partnerSelects } = useDebouncedSearch<any>({
    key: "returns.partner",
    fetcher: (query) => NetworkApi.select({ query }),
    enabled: partnerOpen || !!q.partner,
  });
  useEffect(() => {
    if (!q.partner) {
      setPartnerLabel(null);
      return;
    }
    const found = (partnerSelects ?? []).find((b: any) => b?.id === q.partner);
    if (found) setPartnerLabel(renderBusinessName(found, "-"));
  }, [q.partner, partnerSelects]);

  const params = useMemo(
    () => ({
      page: q.page,
      limit: q.limit,
      ...(q.waiting !== "all" ? { filter: { waiting: q.waiting } } : {}),
      ...(q.stage ? { status: STAGE_STATUSES[Number(q.stage)] } : {}),
      ...(q.partner ? (isBuyerBusiness ? { sellerBusinessId: q.partner } : { buyerBusinessId: q.partner }) : {}),
      ...(q.from ? { dateFrom: `${q.from}T00:00:00` } : {}),
      ...(q.to ? { dateTo: `${q.to}T23:59:59` } : {}),
      ...(q.q ? { search: q.q } : {}),
    }),
    [q, isBuyerBusiness],
  );

  const { data, error, isLoading, isValidating, mutate } = useSwr<any>(
    `returns.queue?${qs.stringify(params)}`,
    async () => await ReturnsApi.list(params),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const rows: any[] = useMemo(() => (Array.isArray(data?.returns) ? data.returns : []), [data]);
  const total: number = data?.pagination?.total ?? 0;
  const summary = data?.summary ?? null;
  const showError = !!error && !isValidating;
  const showSkeleton = isLoading || (isValidating && !data && !error);
  const kpiReady = !!summary && !showError;
  const dash = t("common:list.dash");
  const hasFilters = !!(q.stage || q.partner || q.from || q.to || q.q || q.waiting !== DEFAULT_WAITING);

  const money = (v?: number) => (kpiReady ? currencyFormat(Number(v || 0)).replace(/\s*₮$/, "") : dash);

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams(new URLSearchParams(openId ? { ret: openId } : {}), { replace: true });
  };

  const chipClass = (active: boolean) =>
    `${sharedListClasses.filterChip}${active ? ` ${sharedListClasses.filterChipActive}` : ""}`;

  const tabCount = (tab: WaitingTab): number => {
    if (!summary) return 0;
    switch (tab) {
      case "me": return summary.me.count;
      case "them": return summary.them.count;
      case "done": return summary.done.count;
      case "closed": return summary.closed.count;
      default: return summary.all;
    }
  };
  const tabLabel = (tab: WaitingTab) => (
    <>
      {t(tab === "them" && isBuyerBusiness ? "returns:queue.tabs.themBuyer" : `returns:queue.tabs.${tab}`)}
      {tab === "me" && tabCount(tab) > 0 ? (
        <Text span className={classes.tabBadge}>{tabCount(tab)}</Text>
      ) : (
        <Text span className={classes.tabCount}>{tabCount(tab)}</Text>
      )}
    </>
  );

  const headers: { key: string; right?: boolean }[] = [
    { key: "number" },
    { key: "party" },
    { key: "reason" },
    { key: "amount", right: true },
    { key: "resolution" },
    { key: "progress" },
  ];

  const renderRow = (r: any) => {
    const isSeller = !!myBusinessId && r.sellerBusinessId === myBusinessId;
    const stage = r.stage as ReturnStage;
    const waitingOn = r.waitingOn as WaitingOn;
    const bars = stageBars(stage, waitingOn, !!r.stageAlert);
    const action = waitingOn === "ME" ? nextActionFor(r.status, isSeller) : null;
    const days = Number(r.daysSinceRequest ?? 0);
    const finished = stage === "DONE" || stage === "CLOSED";
    const partyName = isSeller ? r.buyerBusinessName : r.sellerBusinessName;
    const resKey = resolutionKey(r);
    const stageKey = getStageKey(stage);
    const dotClass =
      stage === "DONE" ? classes.stageDotDone
        : stage === "CLOSED" ? classes.stageDotClosed
          : r.stageAlert ? classes.stageDotWarn
            : waitingOn === "THEM" ? classes.stageDotWait
              : "";
    const scope = [
      r.returnType === "FULL" ? t("returns:queue.scope.full") : t("returns:queue.scope.partial"),
      Number(r.disputeCount) > 0 ? t("returns:queue.scope.disputes", { count: r.disputeCount }) : null,
    ].filter(Boolean).join(" · ");

    return (
      <Table.Tr key={r.returnRequestId} className={`${sharedListClasses.scrollRow} ${classes.clickable}`} onClick={() => openReturn(r.returnRequestId)}>
        <Table.Td className={`${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`} onClick={(e) => e.stopPropagation()}>
          <Button
            size="xs"
            radius="md"
            variant={action && PRIMARY_ACTIONS.includes(action) ? "filled" : action ? "default" : "subtle"}
            color={action && PRIMARY_ACTIONS.includes(action) ? "brand" : "gray"}
            onClick={() => openReturn(r.returnRequestId)}
          >
            {action ? t(`returns:queue.next.${action}`) : t("returns:queue.next.view")}
          </Button>
        </Table.Td>
        <Table.Td>
          <Box className={classes.stack}>
            <Text span className={classes.code}>{r.returnNumber}</Text>
            <Text span className={classes.sub}>
              <Text span className={classes.subCode}>{r.orderNumber ?? dash}</Text>
              {" · "}
              {finished ? dash : (
                <Text span className={days >= OVERDUE_DAYS ? classes.overdue : undefined}>
                  {t("returns:queue.days", { count: days })}
                </Text>
              )}
            </Text>
          </Box>
        </Table.Td>
        <Table.Td>
          <PartyCell name={partyName} />
        </Table.Td>
        <Table.Td>
          <Box className={`${classes.stack} ${classes.reasonStack}`} title={getReasonLabel(r.returnReason)}>
            <Text span className={classes.reason}>{getReasonLabel(r.returnReason)}</Text>
            <Text span className={classes.sub}>{scope}</Text>
          </Box>
        </Table.Td>
        <Table.Td className={classes.amount}>{currencyFormat(Number(r.totalRequestedAmount || 0)).replace(/\s*₮$/, "")}</Table.Td>
        <Table.Td>
          {resKey === "none" ? (
            <Text span className={classes.pillNone}>{t("returns:queue.resolution.none")}</Text>
          ) : (
            <Text span className={`${classes.pill} ${classes[`pill${resKey[0].toUpperCase()}${resKey.slice(1)}`]}`}>
              {t(`returns:queue.resolution.${resKey}`)}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Box className={classes.progress}>
            <Box className={classes.bars}>
              {bars.map((b, i) => (
                <Box key={i} component="span" className={`${classes.bar} ${BAR_CLASS[b]}`} />
              ))}
            </Box>
            <Text span className={classes.stageLine}>
              <Box component="span" className={`${classes.stageDot} ${dotClass}`} />
              <Text span className={classes.stageName}>{t(`returns:queue.stage.${stageKey}`)}</Text>
              {waitingOn !== "NONE" && (
                <Text span className={waitingOn === "ME" ? classes.whoMe : classes.whoThem}>
                  {"· "}
                  {t(`returns:queue.who.${waitingOn === "ME" ? "me" : "them"}`)}
                </Text>
              )}
            </Text>
          </Box>
        </Table.Td>
      </Table.Tr>
    );
  };

  const listStates = resolveListStates(
    <ListStates
      loading={showSkeleton}
      error={showError ? error : undefined}
      onRetry={() => mutate()}
      empty={showError || showSkeleton || rows.length === 0}
      filtered={hasFilters}
      query={q.q}
      onClear={clearFilters}
      columns={COLUMN_COUNT}
      emptyTitle={q.waiting === "me" ? t("returns:queue.emptyMe") : t("returns:queue.empty")}
    />
  );

  const renderBody = () => {
    return rows.map(renderRow);
  };

  // /refund-invoice/new is gated on RETURN_CREATE (order-service return/create).
  const canCreateReturn = usePermission("RETURN_CREATE").hasAccess;
  const kpiOldest = summary?.me?.oldestDays ?? 0;

  return (
    <Page>
      <PageHeader
        title={t("returns:title")}
        subtitle={t("returns:subtitle")}
        extra={
          isBuyerBusiness && canCreateReturn ? (
            <Button color="brand" leftSection={<PlusIcon size={14} weight="bold" />} onClick={() => navigate("/refund-invoice/new")}>
              {t("returns:actions.new")}
            </Button>
          ) : undefined
        }
      />

      <Box className={classes.kpi}>
        <Box className={`${classes.kpiCell} ${classes.kpiWarm}`}>
          <Text span className={classes.kpiLabel}><Box component="span" className={classes.kpiDot} style={{ "--dot": "var(--mantine-color-yellow-7)" } as React.CSSProperties} />{t("returns:queue.kpi.me")}</Text>
          <Box className={classes.kpiValueRow}>
            <Text span className={classes.kpiValue}>{money(summary?.me?.amount)}</Text>
            <Text span className={classes.kpiUnit}>{kpiReady ? t("returns:queue.kpi.meUnit", { count: summary.me.count }) : ""}</Text>
          </Box>
          <Text span className={classes.kpiHint}>
            {kpiReady ? (kpiOldest > 0 ? t("returns:queue.kpi.meHint", { days: kpiOldest }) : t("returns:queue.kpi.meHintNone")) : " "}
          </Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}><Box component="span" className={classes.kpiDot} style={{ "--dot": "var(--mantine-color-gray-7)" } as React.CSSProperties} />{t("returns:queue.kpi.them")}</Text>
          <Box className={classes.kpiValueRow}>
            <Text span className={classes.kpiValue}>{money(summary?.them?.amount)}</Text>
            <Text span className={classes.kpiUnit}>{kpiReady ? t("returns:queue.kpi.themUnit", { count: summary.them.count }) : ""}</Text>
          </Box>
          <Text span className={classes.kpiHint}>{t("returns:queue.kpi.themHint")}</Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}><Box component="span" className={classes.kpiDot} style={{ "--dot": "var(--mantine-color-gray-8)" } as React.CSSProperties} />{t("returns:queue.kpi.refund")}</Text>
          <Box className={classes.kpiValueRow}>
            <Text span className={classes.kpiValue}>{money(summary?.refundPending?.amount)}</Text>
            <Text span className={classes.kpiUnit}>{kpiReady ? t("returns:queue.kpi.refundUnit", { count: summary.refundPending.count }) : ""}</Text>
          </Box>
          <Text span className={classes.kpiHint}>{t("returns:queue.kpi.refundHint")}</Text>
        </Box>
        <Box className={classes.kpiCell}>
          <Text span className={classes.kpiLabel}><Box component="span" className={classes.kpiDot} style={{ "--dot": "var(--mantine-color-green-7)" } as React.CSSProperties} />{t("returns:queue.kpi.done")}</Text>
          <Box className={classes.kpiValueRow}>
            <Text span className={classes.kpiValue}>{money(summary?.done?.amount)}</Text>
            <Text span className={classes.kpiUnit}>{kpiReady ? t("returns:queue.kpi.doneUnit", { count: summary.done.count }) : ""}</Text>
          </Box>
          <Text span className={classes.kpiHint}>{t("returns:queue.kpi.doneHint")}</Text>
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.toolbar}>
          <Box className={classes.tabs} role="tablist">
            {WAITING_TABS.map((tab) => (
              <Box
                key={tab}
                component="button"
                type="button"
                role="tab"
                aria-selected={q.waiting === tab}
                data-active={q.waiting === tab || undefined}
                className={classes.tab}
                onClick={() => patchQuery({ waiting: tab })}
              >
                {tabLabel(tab)}
              </Box>
            ))}
          </Box>
          <Box className={classes.chips}>
            <Menu shadow="md" width={200} position="bottom-start" withinPortal>
              <Menu.Target>
                <Box component="button" type="button" className={chipClass(!!q.stage)}>
                  {q.stage ? (
                    <>
                      <Text span size="sm" fw={500}>{t(`returns:queue.stage.s${q.stage}`)}</Text>
                      <XIcon size={12} weight="bold" onClick={(e) => { e.stopPropagation(); patchQuery({ stage: "" }); }} />
                    </>
                  ) : (
                    <>
                      <PlusIcon size={12} weight="bold" />
                      <Text span size="sm">{t("returns:queue.chip.stage")}</Text>
                    </>
                  )}
                </Box>
              </Menu.Target>
              <Menu.Dropdown>
                {Object.keys(STAGE_STATUSES).map((s) => (
                  <Menu.Item key={s} onClick={() => patchQuery({ stage: s })}>{t(`returns:queue.stage.s${s}`)}</Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>

            <Popover opened={partnerOpen} onChange={setPartnerOpen} position="bottom-start" shadow="md" withinPortal>
              <Popover.Target>
                <Box component="button" type="button" className={chipClass(!!q.partner)} onClick={() => setPartnerOpen((o) => !o)}>
                  {q.partner ? (
                    <>
                      <Text span size="sm" fw={500}>{partnerLabel ?? t(isBuyerBusiness ? "returns:queue.chip.partnerBuyer" : "returns:queue.chip.partner")}</Text>
                      <XIcon size={12} weight="bold" onClick={(e) => { e.stopPropagation(); patchQuery({ partner: null }); setPartnerOpen(false); }} />
                    </>
                  ) : (
                    <>
                      <PlusIcon size={12} weight="bold" />
                      <Text span size="sm">{t(isBuyerBusiness ? "returns:queue.chip.partnerBuyer" : "returns:queue.chip.partner")}</Text>
                    </>
                  )}
                </Box>
              </Popover.Target>
              <Popover.Dropdown>
                <Select
                  searchable
                  clearable
                  comboboxProps={{ withinPortal: false }}
                  filter={serverOptionsFilter}
                  searchValue={partnerSearch}
                  onSearchChange={setPartnerSearch}
                  nothingFoundMessage={t("common:noResultsShort")}
                  data={(partnerSelects ?? []).map((item: any) => ({ label: renderBusinessName(item, ""), value: item?.id ?? "" }))}
                  value={q.partner}
                  onChange={(id) => {
                    const found = (partnerSelects ?? []).find((b: any) => b?.id === id);
                    setPartnerLabel(found ? renderBusinessName(found, "-") : null);
                    patchQuery({ partner: id });
                    setPartnerOpen(false);
                  }}
                  placeholder={t(isBuyerBusiness ? "returns:queue.chip.partnerBuyer" : "returns:queue.chip.partner")}
                  w={280}
                />
              </Popover.Dropdown>
            </Popover>

            <DateRangeChip
              label={t("returns:queue.chip.date")}
              value={[q.from, q.to] as DateRangeValue}
              onChange={([from, to]) => patchQuery({ from, to })}
              size="compact"
            />

            {hasFilters && (
              <Button variant="subtle" color="gray" size="compact-sm" onClick={clearFilters}>
                {t("returns:queue.clear")}
              </Button>
            )}
          </Box>

          <TextInput
            className={classes.search}
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            placeholder={t(isBuyerBusiness ? "returns:queue.searchBuyer" : "returns:queue.search")}
            aria-label={t(isBuyerBusiness ? "returns:queue.searchBuyer" : "returns:queue.search")}
            leftSection={<MagnifyingGlassIcon size={16} color="var(--mantine-color-gray-6)" />}
            size="sm"
            radius="md"
          />
        </Box>

        {listStates ?? (
        <Table.ScrollContainer minWidth={1200}>
          <Table className={classes.table} withRowBorders={false} verticalSpacing={0} horizontalSpacing={0} tabularNums>
            <Table.Thead>
              <Table.Tr className={sharedListClasses.scrollRow}>
                <Table.Th className={`${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge} ${classes.nextCol}`}>
                  <Text className={classes.th}>{t("returns:queue.col.next")}</Text>
                </Table.Th>
                {headers.map((h) => (
                  <Table.Th key={h.key} ta={h.right ? "right" : "left"} className={classes.th}>
                    {t(`returns:queue.col.${h.key}`)}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{renderBody()}</Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        )}

        {!showError && (
          <ListFooter
            from={Math.min(total, (q.page - 1) * q.limit + 1)}
            to={Math.min(total, (q.page - 1) * q.limit + rows.length)}
            total={total}
            page={q.page}
            pageSize={q.limit}
            onPageChange={(p) => patchQuery({ page: p }, false)}
            onPageSizeChange={(limit) => patchQuery({ page: 1, limit })}
          />
        )}
      </Box>

      <ReturnDecisionDrawer returnId={openId} onClose={closeReturn} onChanged={() => mutate()} />
    </Page>
  );
};
